import { memo, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../../components/BodyCopy";
import { AuraLayout, Button, Layout, PageTitle, AddCardIcon } from "lola-framework-ui-test";
import { ErrorAlertIcon as ErrorIcon } from "../../assets/icons";
import { FlowHeroMark } from "../../components/FlowHeroMark";
import { PAYOUT_CLAIM_ROUTES, TITLE_COLOR } from "../../constants";
import { useStore } from "../../store/globalState";
import { paymentType } from "../../interfaces/payment";
import { ScrollFadeIndicator } from "../../components/ScrollFadeIndicator";

type ProcessingVariant =
  | "loading"
  | "validationFailed"
  | "pushNotSupported"
  | "underReview";

const variantFromPath = (pathname: string): ProcessingVariant => {
  if (pathname.includes("validation-failed")) return "validationFailed";
  if (pathname.includes("push-not-supported")) return "pushNotSupported";
  if (pathname.includes("under-review")) return "underReview";
  return "loading";
};

/**
 * Destination card pre-validate transition (gallery).
 * Default: spinner then back to Transfer. Error path variants are static.
 */
export const AddCardProcessingPage = memo(() => {
  const { t } = useTranslation("payout-claim");
  const { theme, setPaymentState } = useStore();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const variant = variantFromPath(pathname);
  const colorConfig =
    variant === "loading" ? theme.whiteView : theme.errorView;
  const {
    backgroundBtn,
    textColorBtn,
    dropzoneColors,
    bodyCopy,
    backgroundIcon,
    iconColors,
    title,
  } = colorConfig;
  const advancedRef = useRef(false);

  useEffect(() => {
    if (variant !== "loading" || advancedRef.current) return;
    advancedRef.current = true;
    const timer = setTimeout(() => {
      setPaymentState({
        payment: {
          cardId: "pm_claim_new",
          type: paymentType.CARD,
          brand: "VISA-1111",
        },
      });
      navigate(PAYOUT_CLAIM_ROUTES.TRANSFER, { replace: true });
    }, 1600);
    return () => clearTimeout(timer);
  }, [variant, navigate, setPaymentState]);

  if (variant !== "loading") {
    const messageKey =
      variant === "pushNotSupported"
        ? "card.pushNotSupported"
        : variant === "underReview"
          ? "card.underReview"
          : "card.validationFailed";

    return (
      <AuraLayout colorConfig={colorConfig} className="flow-landing-page">
        <Layout.Content className="landing-section flow-hero-section">
          <ScrollFadeIndicator color={colorConfig.bodyCopy} />
          <section className="flow-hero">
            <FlowHeroMark backgroundIcon={backgroundIcon}>
              <ErrorIcon size={60} colors={iconColors} />
            </FlowHeroMark>
            <section className="landing-container">
              <PageTitle
                highlight={t("card.registrationFailedTitle")}
                highlightColor={title}
                secudnary={t("card.registrationFailedSubtitle")}
                secudnaryColor={title}
              />
              <BodyCopy textColor={bodyCopy}>{t(messageKey)}</BodyCopy>
            </section>
          </section>
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section">
            <Button
              size="large"
              background={backgroundBtn}
              color={textColorBtn}
              onClick={() => navigate(PAYOUT_CLAIM_ROUTES.ADD_CARD)}
            >
              {t("card.tryAgain")}
            </Button>
          </section>
        </Layout.Footer>
      </AuraLayout>
    );
  }

  return (
    <AuraLayout colorConfig={colorConfig} className="flow-landing-page">
      <Layout.Content className="landing-section flow-hero-section">
        <ScrollFadeIndicator color={colorConfig.bodyCopy} />
        <section className="flow-hero">
          <FlowHeroMark
            showSpinner
            dropzoneColors={dropzoneColors}
            backgroundIcon={backgroundIcon}
          >
            <AddCardIcon size={60} colors={dropzoneColors} />
          </FlowHeroMark>
          <section className="landing-container">
            <PageTitle
              highlight={t("card.addingCard")}
              highlightColor={TITLE_COLOR}
              secudnary={t("card.pleaseWait")}
              secudnaryColor={TITLE_COLOR}
            />
          </section>
        </section>
      </Layout.Content>
    </AuraLayout>
  );
});
